import { useEffect } from "react";

/**
 * Bind a chat program-ID override to the lifetime of the calling component.
 *
 * Pages that want chat scoped to a specific program (rather than the
 * user's first-enrolled fallback) can call this hook with the program's
 * ID — the override is set on mount, refreshed when `programId` changes,
 * and cleared on unmount. The unmount cleanup is the important part: it
 * guarantees the override doesn't leak into whatever route renders next.
 *
 * Replaces the per-page `useEffect(() => { setOverride(id); return () =>
 * setOverride(null) })` pattern, which puts the cleanup contract in the
 * caller's hands and is easy to get wrong (forget to return the cleanup,
 * include a stale dependency, drop the call when refactoring). Centralising
 * it here means every consumer gets identical semantics.
 *
 * @param programId The program to scope chat to. Pass `null` (or the
 *   value before the data has loaded) to leave the override clear.
 * @param setOverride The store setter — usually wired in the calling
 *   component as `usePlatformStore(s => s.setProgramIdOverride)`. Passed
 *   in rather than imported so this hook stays in `@core` and doesn't
 *   reach upward into the platform store.
 */
export function useScopedProgramOverride(
  programId: number | null,
  setOverride: (id: number | null) => void
): void {
  useEffect(() => {
    setOverride(programId);
    return () => setOverride(null);
  }, [programId, setOverride]);
}

export default useScopedProgramOverride;
